/**
 * SimpleSemaphore – a tiny, zero-dependency counting semaphore.
 *
 * Used to throttle concurrent HTTP calls.  In our scraper we instantiate it
 * with `new SimpleSemaphore(1)` so **only one request is ever in flight**,
 * but you can bump that number for parallelism if the API allows.
 *
 * Example
 * ───────
 * const sem = new simpleSemaphore(2);
 *
 * await sem.acquire();
 * try {
 *   // perform async work …
 * } finally {
 *   sem.release();   // MUST be in finally to avoid dead-locks!
 * }
 */
export class simpleSemaphore {
    private queue: (() => void)[] = [];
    private used = 0;

    constructor(private readonly maxConcurrent = 1) { }

    /** Wait (asynchronously) until a slot is free. */
    acquire(): Promise<void> {
        if (this.used < this.maxConcurrent) {
            this.used++;
            return Promise.resolve();
        }
        return new Promise((resolve) => {
            this.queue.push(() => {
                this.used++;
                resolve();
            });
        });
    }

    /** Free one slot and wake exactly one queued waiter (FIFO). */
    release() {
        this.used = Math.max(0, this.used - 1);
        const next = this.queue.shift();
        if (next) next();
    }
}
